import { useId, type JSX } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ColumnDef, SortDirection, SortState } from '@/shared/types/table-filters.types';

export interface TableSortSelectProps<T> {
  columns: ColumnDef<T>[];
  sort: SortState | null;
  onSortChange: (sort: SortState | null) => void;
  label?: string;
}

/** Sentinel option value meaning "fall back to the list's default order". */
const DEFAULT_SORT_VALUE = 'default';

const DIRECTIONS: SortDirection[] = ['asc', 'desc'];

const DIRECTION_LABELS: Record<SortDirection, string> = {
  asc: 'ascending',
  desc: 'descending',
};

interface SortOption {
  value: string;
  label: string;
}

function encode(sort: SortState): string {
  return `${sort.key}:${sort.dir}`;
}

function decode(value: string): SortState | null {
  const [key, dir] = value.split(':');
  if (!key || (dir !== 'asc' && dir !== 'desc')) return null;
  return { key, dir };
}

function sortOptions<T>(columns: ColumnDef<T>[]): SortOption[] {
  return columns
    .filter((column) => column.sortable)
    .flatMap((column) =>
      DIRECTIONS.map((dir) => ({
        value: encode({ key: column.key, dir }),
        label: `${column.label} ${DIRECTION_LABELS[dir]}`,
      }))
    );
}

/**
 * Sort control for lists rendered as cards, where there is no column header to click.
 * Table-shaped lists use `SortableTableHead` instead.
 */
export function TableSortSelect<T>({
  columns,
  sort,
  onSortChange,
  label = 'Sort',
}: TableSortSelectProps<T>): JSX.Element {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={sort ? encode(sort) : DEFAULT_SORT_VALUE}
        onValueChange={(value) => onSortChange(decode(value))}
      >
        <SelectTrigger id={id} aria-label="Sort by" className="w-full sm:w-[220px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT_SORT_VALUE}>Default order</SelectItem>
          {sortOptions(columns).map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
